import StatCard from './StatCard';
import { STATUS_LABEL } from '@shared/constants';

/**
 * V65 — Fila de tarjetas por estado, EXHAUSTIVA POR CONSTRUCCIÓN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 EL BUG QUE ESTE COMPONENTE EXISTE PARA HACER IMPOSIBLE
 * ═══════════════════════════════════════════════════════════════════════════
 * Ocho pantallas mostraban un "Total" que no cuadraba con sus tarjetas. La causa NO era
 * que alguien olvidara un estado: era que **el total se calculaba con una expresión
 * distinta de la suma de las partes**. Tres ejemplos reales, los tres del mismo día:
 *
 *   HistoryPage         Total = total                              tarjetas: 5 estados
 *   SupervisorListPage  Total = receiptGroups.length               tarjetas: 4 estados
 *   ReviewerListPage    Total = receipts.filter(s !== 'draft')     tarjetas: 2 estados
 *
 * Con el total y el desglose calculados por separado, que coincidan es una coincidencia
 * que hay que mantener a mano cada vez que aparece un estado nuevo — y `paid` apareció
 * y nadie actualizó ninguna de las ocho.
 *
 * ⚠⚠ ACÁ EL TOTAL NO SE RECIBE: SE DERIVA. El componente recibe la LISTA y agrupa por
 * estado él mismo, así que cada fila cae en exactamente un balde y
 * `total === Σ tarjetas` es una identidad, no algo que recordar. Un estado nuevo que
 * aparezca en los datos se dibuja SOLO, aunque nadie lo haya previsto.
 *
 * ⚠ `extras` va SEPARADO a propósito. Es para las tarjetas que NO son sumables: un
 * subconjunto de otra ("Mi Aprobación", "Por Revisar") o algo de otro universo. Meterlas
 * en la fila es el otro modo de romper el cuadre, y se ve idéntico al primero.
 */
export default function StatusStatRow({
  items,
  conteos,
  estadoDe = (x) => x.status,
  agrupar = (st) => st,
  orden = [],
  etiquetas = {},
  subtitulos = {},
  destacado,
  excluir = [],
  filtro,
  onFiltrar,
  extras = null,
  etiquetaTotal = 'Total',
  subtituloTotal,
}) {
  // Dos entradas posibles y UNA sola semántica. `items` es la lista cruda; `conteos` es
  // `[{status, count}]`, que es lo que devuelven los endpoints de dashboard — pedirles la
  // lista entera solo para contarla sería traer miles de filas para pintar seis tarjetas.
  // ⚠ La garantía se conserva en las dos: el total SALE de los mismos baldes, así que
  // sigue siendo la suma por identidad y no un número recibido aparte.
  const grupos = new Map();
  let visibles = 0;
  if (conteos) {
    for (const c of conteos) {
      const e = agrupar(c.status) ?? 'sin_estado';
      if (excluir.includes(c.status) || excluir.includes(e)) continue;
      grupos.set(e, (grupos.get(e) || 0) + Number(c.count || 0));
      visibles += Number(c.count || 0);
    }
  } else {
    for (const x of (items || [])) {
      const e = estadoDe(x) ?? 'sin_estado';
      if (excluir.includes(e) || excluir.includes(x.status)) continue;
      grupos.set(e, (grupos.get(e) || 0) + 1);
      visibles += 1;
    }
  }

  // Orden canónico primero; cualquier estado no previsto se agrega al final en vez de
  // desaparecer — que desaparezca es justo lo que producía el descuadre.
  const estados = [
    ...orden.filter(e => grupos.has(e)),
    ...[...grupos.keys()].filter(e => !orden.includes(e)).sort(),
  ];

  const cuenta = (e) => grupos.get(e) || 0;
  const seleccionado = (e) => filtro && filtro.size === 1 && filtro.has(e);
  const clic = (e) => onFiltrar ? () => onFiltrar(new Set([e])) : undefined;

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: extras ? 10 : 20 }}>
      <StatCard
        label={etiquetaTotal}
        value={visibles}
        subtitle={subtituloTotal}
        subtitleLarge={!!subtituloTotal}
        onClick={onFiltrar ? () => onFiltrar(new Set()) : undefined}
        active={filtro ? filtro.size === 0 : undefined}
      />
      {estados.map(e => (
        <StatCard
          key={e}
          label={etiquetas[e] || STATUS_LABEL[e] || e}
          value={cuenta(e)}
          subtitle={subtitulos[e]}
          subtitleLarge={!!subtitulos[e]}
          highlight={destacado === e}
          onClick={clic(e)}
          active={seleccionado(e)}
        />
      ))}
      {extras}
    </div>
  );
}
